import type { LinkRegistry, Pathway } from "../types";

import { links } from "../../linksRegistry";


export const soreThroatPathway: Pathway = {
  id: "sorethroat",
  name: "Acute sore throat",
  population: "Adults and children aged 5+ (pregnant individuals must be 16+)",
  pgdRefs: [
    "pgd-5a-penv-sorethroat",
    "pgd-5b-clarithromycin-sorethroat",
    "pgd-5c-erythromycin-sorethroat",
  ],
  links,
  clinicalNotes: [
    "Acute sore throat can last around 1 week, but most people will get better within this time without antibiotics, regardless of cause.",
    "Antibiotics make little difference to how long symptoms last and withholding antibiotics is unlikely to lead to complications.",
    "Regular or increasing repeat purchase/request of throat lozenges or medicated throat sprays should trigger further questioning.",
  ],
  clinicalNoteLinkRefs: ["nice-cks-sore-throat", "nhs-england-pharmacy-first-clinical-pathways", "feverpain-tool", "pgd-sore-throat-phenoxymethylpenicillin", "pgd-sore-throat-clarithromycin", "pgd-sore-throat-erythromycin"],
  demographicEligibility: {
    minAge: 5,
    blockMessage: "This pathway is for patients aged 5 years and over. Patients under 5 should be referred to their GP.",
  },
  demographicConfiguration: {
    bannerFields: [
      { field: "dateOfBirth", label: "Date of birth", type: "date" },
      { field: "ageYears", label: "Age (years)", type: "number" },
      {
        field: "knownAllergies",
        label: "Known allergies",
        type: "string",
        helpText: "Penicillin allergy is asked again in the flow",
      },
    ],
  },
  exclusionPrefillMap: {},
  entryNode: "q1_visit_type",
  nodes: [
    {
      id: "q1_visit_type",
      type: "yes_no",
      text: "Is this a follow-up consultation for a sore throat assessed previously?",
      hint: "Ask manually for now. Previous Pharmacy First sore throat consultation detection can be added later.",
      required: true,
      storesTo: "isFollowUp",
      yes: "q1a_followup_load",
      no: "q2_red_flags",
    },
    {
      id: "q1a_followup_load",
      type: "yes_no",
      text: "Original consultation record loaded and confirmed?",
      hint: "Confirm original FeverPAIN score, date, and outcome from the patient record where available.",
      required: true,
      yes: "q2_red_flags",
      no: { outcomeId: "OUT_NO_RECORD" },
    },
    {
      id: "q2_red_flags",
      type: "checklist",
      text: "Any emergency red flag signs?",
      showSepsisWarning: true,
      items: [
        { id: "epiglottitis", text: "Suspected epiglottitis (4 Ds: dysphagia, dysphonia, drooling, distress + stridor) — do not examine throat" },
        { id: "stridor", text: "Stridor (noisy or high-pitched sound when breathing)" },
        { id: "sepsis", text: "Signs of sepsis or severe/life-threatening infection" },
        { id: "complications", text: "Severe complications — clinical dehydration, signs of pharyngeal abscess" },
      ],
      includeNoneOption: true,
      anySelected: "GATEWAY_EMERGENCY",
      noneSelected: "q3_differential",
    },
    {
      id: "GATEWAY_EMERGENCY",
      type: "gateway_alarm",
      heading: "EMERGENCY — call 999 or direct to A&E",
      body: "Do not examine the throat if epiglottitis is suspected. Consider calculating NEWS2 score.",
      action: "close",
      triggersPayment: true,
      linkedCalculatorId: "news2",
      linkedCalculatorLabel: "Calculate NEWS2 score (optional)",
    },
    {
      id: "q3_differential",
      type: "checklist",
      text: "Any of the following suspected alternative diagnoses?",
      items: [
        { id: "scarlet", text: "Scarlet fever (strawberry tongue, sandpaper rash, bright red skin in creases)" },
        { id: "quinsy", text: "Quinsy / peritonsillar abscess (trismus, displaced uvula, unilateral tonsillar swelling)" },
        { id: "glandular", text: "Glandular fever (extreme tiredness, tonsillitis not improving, especially teenagers/young adults)" },
        { id: "diphtheria", text: "Diphtheria (thick grey-white throat coating, difficulty breathing/swallowing)" },
        { id: "cancer", text: "Possible cancer red flags (persistent mouth ulcers, mass/unilateral swelling, unable to swallow, bleeding/numbness, red/white patches, hoarse voice >3 weeks in over 45s)" },
      ],
      includeNoneOption: true,
      anySelected: { outcomeId: "OUT_REFER_DIFFERENTIAL" },
      noneSelected: "q4_age",
      clinicalLinks: [
        { label: "NICE CKS — Scarlet fever", url: links["nice-cks-scarlet-fever"].url, context: "reference" },
        { label: "NICE CKS — Glandular fever", url: links["nice-cks-glandular-fever"].url, context: "reference" },
      ],
    },
    {
      id: "q4_age",
      type: "yes_no",
      text: "Is the patient aged 5 or over?",
      required: true,
      autoAnswer: { when: { patientAgeAtLeast: 5 }, value: true },
      yes: "q5_pregnant_check",
      no: { outcomeId: "OUT_CANNOT_UNDER5" },
    },
    {
      id: "q5_pregnant_check",
      type: "yes_no",
      text: "Is the patient currently pregnant or suspected pregnant?",
      required: true,
      storesTo: "isPregnant",
      autoAnswer: { when: { patientIsFemale: false }, value: false },
      yes: "q5_pregnant_age_gate",
      no: "q6_immuno",
    },
    {
      id: "q5_pregnant_age_gate",
      type: "condition_route",
      conditions: [
        { when: { patientAgeUnder: 16 }, then: { outcomeId: "OUT_CANNOT_PREGNANT_UNDER16" } },
      ],
      default: "q6_immuno",
    },
    {
      id: "q6_immuno",
      type: "yes_no",
      text: "Is the patient severely immunosuppressed?",
      required: true,
      yes: { outcomeId: "OUT_REFER_IMMUNO" },
      no: "q7_feverpain",
    },
    {
      id: "q7_feverpain",
      type: "score",
      text: "Score each FeverPAIN criterion present",
      storesTo: "feverPainScore",
      items: [
        { id: "fp_fever", text: "Fever (high temperature) during previous 24 hours", points: 1 },
        { id: "fp_purulent", text: "Purulent tonsils", points: 1 },
        { id: "fp_onset", text: "Attend rapidly — symptom onset ≤3 days ago", points: 1 },
        { id: "fp_inflamed", text: "Severely inflamed tonsils", points: 1 },
        { id: "fp_no_cough", text: "No cough or coryza", points: 1 },
      ],
      clinicalLinks: [
        { label: "NICE CKS — FeverPAIN clinical features", url: links["nice-cks-feverpain-clinical-features"].url, context: "reference" },
      ],

      routing: (score: number, _patient, context) => {
        if (score <= 1) return { outcomeId: "OUT_SELF_CARE_FP01" };
        if (score <= 3) {
          // Follow-up: route through payment gateway before reassessment.
          // Initial: self-care is first-line, no payment triggered.
          return context?.isFollowUp ? "GATEWAY_ELIGIBLE_FP23" : { outcomeId: "OUT_SELF_CARE_FP23" };
        }
        return "GATEWAY_ELIGIBLE_FP45";
      },
    },
    {
      id: "GATEWAY_ELIGIBLE_FP23",
      type: "gateway_progression",
      heading: "Follow-up reassessment — antibiotic may be appropriate",
      body: "FeverPAIN score 2–3 (follow-up): Clinician to reassess whether antibiotic treatment is now appropriate.",
      triggersPayment: true,
      next: "q_followup_impression",
    },
    {
      id: "GATEWAY_ELIGIBLE_FP45",
      type: "gateway_progression",
      heading: "Antibiotic treatment indicated",
      body: "FeverPAIN score 4–5: Antibiotic treatment is appropriate.",
      triggersPayment: true,
      next: "q8_severity",
    },
    {
      id: "q8_severity",
      type: "severity",
      text: "Overall symptom severity?",
      mild: { outcomeId: "OUT_SELF_CARE_FP45_MILD" },
      moderate: "q9_allergy",
      severe: "q9_allergy",
    },
    {
      id: "q_followup_impression",
      type: "yes_no",
      text: "Based on your clinical reassessment, is antibiotic treatment now appropriate for this patient?",
      required: true,
      yes: "q9_allergy",
      no: "q_followup_disposition",
    },
    {
      id: "q_followup_disposition",
      type: "yes_no",
      text: "Does this patient require onward referral?",
      required: true,
      yes: { outcomeId: "OUT_REFER_FOLLOWUP" },
      no: { outcomeId: "OUT_SELF_CARE_FOLLOWUP" },
    },
    {
      id: "q9_allergy",
      type: "yes_no",
      text: "Does the patient have a known penicillin allergy? (Including allergy to flucloxacillin or severe hypersensitivity to any beta-lactam)",
      required: true,
      storesTo: "hasPenicillinAllergy",
      yes: "drug_select",
      no: "drug_select",
    },
    {
      id: "drug_select",
      type: "drug_selection",
      variant: "auto",
      rules: [
        { when: { hasPenicillinAllergy: false, isPregnant: false }, pgdId: "pgd-5a-penv-sorethroat" },
        { when: { hasPenicillinAllergy: false, isPregnant: true }, pgdId: "pgd-5a-penv-sorethroat" },
        { when: { hasPenicillinAllergy: true, isPregnant: false }, pgdId: "pgd-5b-clarithromycin-sorethroat" },
        { when: { hasPenicillinAllergy: true, isPregnant: true }, pgdId: "pgd-5c-erythromycin-sorethroat" },
      ],
      next: "weight_capture",
    },
    {
      id: "weight_capture",
      type: "weight_capture",
      text: "Confirm patient weight (kg) for paediatric clarithromycin dosing",
      hint: "Only required for children aged 5–11 receiving clarithromycin under PGD 5b.",
      minKg: 0.5,
      maxKg: 250,
      next: "pgd_exclusions",
    },
    {
      id: "pgd_exclusions",
      type: "pgd_exclusion_check",
      anyTriggered: { outcomeId: "OUT_CANNOT_SUPPLY" },
      allCleared: { outcomeId: "OUT_SUPPLY" },
    },
  ],
  outcomes: {
    OUT_SUPPLY: {
      id: "OUT_SUPPLY",
      type: "outcome",
      outcomeType: "supply",
      title: "Supply antibiotic under PGD",
      reason: "Eligibility criteria met and PGD pre-supply checks cleared.",
      safetyNetting: [
        "If symptoms worsen rapidly or significantly at any time, seek urgent medical advice",
        "If symptoms do not improve after completion of the course, return to pharmacy or contact GP",
      ],
      linkRefs: ["target-rti-leaflet", "nhs-sore-throat"],
      leafletRefs: ["leaflet-sorethroat-en", "leaflet-sorethroat-cy", "leaflet-cough-en"],
    },
    OUT_SELF_CARE_FP01: {
      id: "OUT_SELF_CARE_FP01",
      type: "outcome",
      outcomeType: "self_care",
      title: "FeverPAIN 0–1 — self-care",
      reason: "Antibiotic is not needed. Offer OTC treatment for symptomatic relief.",
      safetyNetting: [
        "Return to pharmacy after 1 week if no improvement for pharmacist reassessment",
        "Seek urgent help if symptoms worsen rapidly at any time",
      ],
      linkRefs: ["target-rti-leaflet", "nhs-sore-throat"],
      leafletRefs: ["leaflet-sorethroat-en", "leaflet-sorethroat-cy", "leaflet-cough-en"],
    },
    OUT_SELF_CARE_FP23: {
      id: "OUT_SELF_CARE_FP23",
      type: "outcome",
      outcomeType: "self_care",
      title: "FeverPAIN 2–3 — self-care",
      reason: "Antibiotic is not needed initially. Offer OTC treatment and safety netting.",
      safetyNetting: [
        "Return to pharmacy within 3–5 days if no improvement for pharmacist reassessment",
        "Seek urgent help if symptoms worsen rapidly at any time",
      ],
      linkRefs: ["target-rti-leaflet", "nhs-sore-throat"],
      leafletRefs: ["leaflet-sorethroat-en", "leaflet-sorethroat-cy", "leaflet-cough-en"],
    },
    OUT_SELF_CARE_FP45_MILD: {
      id: "OUT_SELF_CARE_FP45_MILD",
      type: "outcome",
      outcomeType: "self_care",
      title: "FeverPAIN 4–5, mild symptoms — self-care first",
      reason: "Consider pain relief and self-care as first-line treatment.",
      safetyNetting: [
        "Return to pharmacy within 3–5 days if no improvement for pharmacist reassessment",
        "Seek urgent help if symptoms worsen rapidly at any time",
      ],
      linkRefs: ["target-rti-leaflet", "nhs-sore-throat"],
      leafletRefs: ["leaflet-sorethroat-en", "leaflet-sorethroat-cy", "leaflet-cough-en"],
    },
    OUT_SELF_CARE_FOLLOWUP: {
      id: "OUT_SELF_CARE_FOLLOWUP",
      type: "outcome",
      outcomeType: "self_care",
      title: "Follow-up — continued self-care",
      reason: "After reassessment, antibiotic not considered appropriate and referral not required.",
      safetyNetting: [
        "Return to pharmacy if symptoms worsen at any time",
        "Return if no further improvement within 3–5 days",
        "Seek urgent help if signs of sepsis develop",
      ],
      linkRefs: ["target-rti-leaflet", "nhs-sore-throat"],
      leafletRefs: ["leaflet-sorethroat-en", "leaflet-sorethroat-cy", "leaflet-cough-en"],
    },
    OUT_REFER_DIFFERENTIAL: {
      id: "OUT_REFER_DIFFERENTIAL",
      type: "outcome",
      outcomeType: "refer_routine",
      title: "Refer — suspected alternative diagnosis",
      reason: "Signs or symptoms indicating an alternative diagnosis. Refer to GP or other provider.",
      safetyNetting: ["Refer to GP or appropriate provider", "Seek urgent help if symptoms worsen rapidly"],
      linkRefs: ["nice-cks-scarlet-fever", "nice-cks-glandular-fever"],
      leafletRefs: ["leaflet-sorethroat-en", "leaflet-sorethroat-cy", "leaflet-cough-en"],
    },
    OUT_REFER_IMMUNO: {
      id: "OUT_REFER_IMMUNO",
      type: "outcome",
      outcomeType: "refer_urgent",
      title: "Refer urgently — severely immunosuppressed",
      reason: "Refer to A&E for further assessment.",
      safetyNetting: ["Refer same day", "Call 999 if signs of sepsis or rapid deterioration"],
      leafletRefs: ["leaflet-sorethroat-en", "leaflet-sorethroat-cy", "leaflet-cough-en"],
    },
    OUT_REFER_FOLLOWUP: {
      id: "OUT_REFER_FOLLOWUP",
      type: "outcome",
      outcomeType: "refer_routine",
      title: "Follow-up — refer to GP",
      reason: "After reassessment, pharmacist considers onward referral appropriate.",
      safetyNetting: ["Refer to GP or other provider", "Seek urgent help if symptoms worsen rapidly"],
      leafletRefs: ["leaflet-sorethroat-en", "leaflet-sorethroat-cy", "leaflet-cough-en"],
    },
    OUT_CANNOT_SUPPLY: {
      id: "OUT_CANNOT_SUPPLY",
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Cannot supply — PGD exclusion met",
      reason: "One or more PGD pre-supply exclusions were triggered.",
      safetyNetting: ["Document the exclusion(s) that prevented supply", "Refer to prescriber if antibiotic appropriate"],
      linkRefs: ["target-rti-leaflet", "nhs-sore-throat"],
      leafletRefs: ["leaflet-sorethroat-en", "leaflet-sorethroat-cy", "leaflet-cough-en"],
    },
    OUT_CANNOT_UNDER5: {
      id: "OUT_CANNOT_UNDER5",
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Cannot supply — under 5",
      reason: "Refer to GP or other provider.",
      safetyNetting: ["Refer to GP or appropriate provider"],
      leafletRefs: ["leaflet-sorethroat-en", "leaflet-sorethroat-cy", "leaflet-cough-en"],
    },
    OUT_CANNOT_PREGNANT_UNDER16: {
      id: "OUT_CANNOT_PREGNANT_UNDER16",
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Cannot supply — pregnant and under 16",
      reason: "Refer to GP or other provider.",
      safetyNetting: ["Refer same day", "Return urgently if symptoms worsen"],
      leafletRefs: ["leaflet-sorethroat-en", "leaflet-sorethroat-cy", "leaflet-cough-en"],
    },
    OUT_NO_RECORD: {
      id: "OUT_NO_RECORD",
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Cannot proceed as follow-up — original consultation not found",
      reason: "Locate original record and retry, or treat as initial consultation.",
      safetyNetting: ["Restart as initial consultation if clinically appropriate"],
      leafletRefs: ["leaflet-sorethroat-en", "leaflet-sorethroat-cy", "leaflet-cough-en"],
    },
  },
};
